/**
 * Cloud V2 — Main JavaScript
 * cloudcompany.store
 * Lightweight vanilla JS — no jQuery, no bloat
 */

(function () {
  'use strict';

  /* =========================================================================
     1. Header: Transparent → Solid on Scroll
     ========================================================================= */
  const CloudHeader = {
    el: null,
    scrollThreshold: 80,
    heroPresent: false,

    init() {
      this.el = document.querySelector('.cloud-header');
      if (!this.el) return;

      // Only use transparent mode if hero section is present
      this.heroPresent = !!document.querySelector('.cloud-hero');

      // O HTML já vem com "cloud-header--transparent" fixo (ver
      // sections/header.liquid) — sem hero, isto ficava por remover e o
      // header acabava com as DUAS classes ao mesmo tempo (transparent +
      // solid). Não tinha impacto visível antes (nenhuma regra de cor
      // dependia de --transparent), mas passou a ser um bug real depois de
      // --transparent ganhar uma cor de texto própria (ver CLAUDE.md,
      // contraste do menu sobre o hero) — o texto escuro pensado só para
      // cima do hero claro aparecia também sobre o header sólido escuro
      // das restantes páginas, ilegível.
      if (this.heroPresent) {
        this.el.classList.remove('cloud-header--solid');
        this.el.classList.add('cloud-header--transparent');
      } else {
        this.el.classList.remove('cloud-header--transparent');
        this.el.classList.add('cloud-header--solid');
      }

      this.onScroll = this.handleScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.handleScroll(); // run once
    },

    handleScroll() {
      if (!this.heroPresent) return;
      const scrolled = window.scrollY > this.scrollThreshold;
      this.el.classList.toggle('cloud-header--transparent', !scrolled);
      this.el.classList.toggle('cloud-header--solid', scrolled);
    }
  };

  /* =========================================================================
     2b. Desktop Mega Menu (hover w/ delays + keyboard)
     ========================================================================= */
  const CloudMegaMenu = {
    openDelay: 150,
    closeDelay: 300,

    init() {
      document.querySelectorAll('.cloud-header__nav-item[data-has-megamenu]').forEach((item) => {
        const link = item.querySelector('.cloud-header__nav-link');
        let openT = null;
        let closeT = null;

        const open = () => {
          clearTimeout(closeT);
          openT = setTimeout(() => {
            item.classList.add('cloud-header__nav-item--open');
            if (link) link.setAttribute('aria-expanded', 'true');
          }, this.openDelay);
        };
        const close = () => {
          clearTimeout(openT);
          closeT = setTimeout(() => {
            item.classList.remove('cloud-header__nav-item--open');
            if (link) link.setAttribute('aria-expanded', 'false');
          }, this.closeDelay);
        };

        // Hover (mouse)
        item.addEventListener('mouseenter', open);
        item.addEventListener('mouseleave', close);

        // Keyboard: opens when focus enters, closes when it leaves the item
        item.addEventListener('focusin', () => {
          clearTimeout(closeT);
          item.classList.add('cloud-header__nav-item--open');
          if (link) link.setAttribute('aria-expanded', 'true');
        });
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) {
            item.classList.remove('cloud-header__nav-item--open');
            if (link) link.setAttribute('aria-expanded', 'false');
          }
        });
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            item.classList.remove('cloud-header__nav-item--open');
            if (link) { link.setAttribute('aria-expanded', 'false'); link.focus(); }
          }
        });
      });
    }
  };

  /* =========================================================================
     2. Mobile Navigation
     ========================================================================= */
  const CloudMobileNav = {
    nav: null,
    toggle: null,
    close: null,
    isOpen: false,

    init() {
      this.nav    = document.querySelector('.cloud-mobile-nav');
      this.toggle = document.querySelector('.cloud-header__mobile-toggle');
      this.close  = document.querySelector('.cloud-mobile-nav__close');
      if (!this.nav || !this.toggle) return;

      this.toggle.addEventListener('click', () => this.open());
      if (this.close) this.close.addEventListener('click', () => this.close_nav());

      // Close on backdrop click
      document.addEventListener('click', (e) => {
        if (this.isOpen && !this.nav.contains(e.target) && !this.toggle.contains(e.target)) {
          this.close_nav();
        }
      });

      // Close on ESC
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close_nav();
      });

      // Accordion submenus (Football / Apparel)
      this.nav.querySelectorAll('.cloud-mobile-nav__accordion-trigger').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const submenu = document.getElementById(trigger.getAttribute('aria-controls'));
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          trigger.setAttribute('aria-expanded', String(!isOpen));
          if (submenu) submenu.classList.toggle('cloud-mobile-nav__submenu--open', !isOpen);
        });
      });
    },

    open() {
      this.isOpen = true;
      this.nav.classList.add('cloud-mobile-nav--open');
      document.body.style.overflow = 'hidden';
      this.toggle.setAttribute('aria-expanded', 'true');
    },

    close_nav() {
      this.isOpen = false;
      this.nav.classList.remove('cloud-mobile-nav--open');
      document.body.style.overflow = '';
      this.toggle.setAttribute('aria-expanded', 'false');
    }
  };

  /* =========================================================================
     2c. Hero Carousel (autoplay + fade + dots)
     ========================================================================= */
  const CloudHeroCarousel = {
    init() {
      document.querySelectorAll('.cloud-hero').forEach((hero) => {
        const slides = Array.from(hero.querySelectorAll('.cloud-hero__slide'));
        if (slides.length < 2) return;

        const dots = Array.from(hero.querySelectorAll('.cloud-hero__dot'));
        const seconds = parseFloat(hero.getAttribute('data-autoplay-seconds')) || 5;
        let current = slides.findIndex((s) => s.classList.contains('cloud-hero__slide--active'));
        if (current < 0) current = 0;
        let timer = null;

        const goTo = (index) => {
          const next = ((index % slides.length) + slides.length) % slides.length;
          if (next === current) return;

          slides[current].classList.remove('cloud-hero__slide--active');
          slides[current].setAttribute('aria-hidden', 'true');
          slides[current].setAttribute('tabindex', '-1');

          slides[next].classList.add('cloud-hero__slide--active');
          slides[next].setAttribute('aria-hidden', 'false');
          slides[next].setAttribute('tabindex', '0');

          if (dots[current]) {
            dots[current].classList.remove('cloud-hero__dot--active');
            dots[current].setAttribute('aria-selected', 'false');
          }
          if (dots[next]) {
            dots[next].classList.add('cloud-hero__dot--active');
            dots[next].setAttribute('aria-selected', 'true');
          }

          current = next;
        };

        const start = () => {
          stop();
          timer = setInterval(() => goTo(current + 1), seconds * 1000);
        };
        const stop = () => {
          if (timer) clearInterval(timer);
          timer = null;
        };

        dots.forEach((dot, i) => {
          dot.addEventListener('click', (e) => {
            e.preventDefault();
            goTo(i);
            start(); // reinicia o temporizador do autoplay a partir daqui
          });
        });

        hero.addEventListener('mouseenter', stop);
        hero.addEventListener('mouseleave', start);

        start();
      });
    }
  };

  /* =========================================================================
     3. Horizontal Sliders (New Arrivals, etc.)
     ========================================================================= */
  const CloudSlider = {
    init() {
      document.querySelectorAll('.cloud-slider').forEach(slider => {
        const track   = slider.querySelector('.cloud-slider__track');
        const prevBtn = slider.querySelector('[data-slider-prev]');
        const nextBtn = slider.querySelector('[data-slider-next]');
        if (!track) return;

        const scrollAmount = () => {
          const item = track.querySelector('.cloud-slider__item');
          return item ? item.offsetWidth + parseInt(getComputedStyle(track).gap) : 300;
        };

        if (nextBtn) {
          nextBtn.addEventListener('click', () => {
            track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
          });
        }

        if (prevBtn) {
          prevBtn.addEventListener('click', () => {
            track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
          });
        }

        // Update button states
        const updateButtons = () => {
          if (prevBtn) prevBtn.disabled = track.scrollLeft <= 0;
          if (nextBtn) nextBtn.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        };

        track.addEventListener('scroll', updateButtons, { passive: true });
        updateButtons();

        // Touch swipe support
        let startX = 0;
        track.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
        track.addEventListener('touchend', (e) => {
          const diff = startX - e.changedTouches[0].clientX;
          if (Math.abs(diff) > 50) {
            track.scrollBy({ left: diff > 0 ? scrollAmount() : -scrollAmount(), behavior: 'smooth' });
          }
        }, { passive: true });

        // Autoplay (opt-in via .cloud-slider--autoplay, off for users who prefer reduced motion)
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (slider.classList.contains('cloud-slider--autoplay') && !reduceMotion) {
          const intervalMs = parseInt(slider.dataset.autoplayInterval, 10) || 4000;
          let timer = null;

          const advance = () => {
            const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
            track.scrollTo(atEnd ? { left: 0, behavior: 'smooth' } : { left: track.scrollLeft + scrollAmount(), behavior: 'smooth' });
          };

          const startAutoplay = () => { if (!timer) timer = setInterval(advance, intervalMs); };
          const stopAutoplay = () => { clearInterval(timer); timer = null; };

          startAutoplay();
          slider.addEventListener('mouseenter', stopAutoplay);
          slider.addEventListener('mouseleave', startAutoplay);
          slider.addEventListener('touchstart', stopAutoplay, { passive: true });
          slider.addEventListener('focusin', stopAutoplay);
          slider.addEventListener('focusout', startAutoplay);
        }
      });
    }
  };

  /* =========================================================================
     4. Scroll Reveal Animations
     ========================================================================= */
  const CloudReveal = {
    init() {
      const elements = document.querySelectorAll('.cloud-reveal');
      if (!elements.length) return;

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('cloud-reveal--visible');
              observer.unobserve(entry.target);
            }
          });
        }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

        elements.forEach(el => observer.observe(el));
      } else {
        // Fallback: show all
        elements.forEach(el => el.classList.add('cloud-reveal--visible'));
      }
    }
  };

  /* =========================================================================
     5. Predictive Search
     ========================================================================= */
  const CloudSearch = {
    overlay: null,
    input: null,
    results: null,
    trigger: null,
    closeBtn: null,
    debounceTimer: null,

    // Estado do scroll infinito (ver loadMore()). `searchToken` invalida
    // respostas de fetch atrasadas de uma pesquisa anterior — sem isto, uma
    // 1ª pesquisa lenta podia responder DEPOIS de uma 2ª mais rápida e
    // sobrescrever os resultados certos com os errados.
    currentQuery: null,
    searchToken: 0,
    nextPage: 2,
    loadingMore: false,
    noMoreResults: false,

    // Página de segurança: ao fim de 20 páginas (~200 produtos) para de
    // pedir mais automaticamente, para uma pesquisa com milhares de
    // resultados nunca disparar centenas de fetches sozinha. O link "Ver
    // todos os resultados" continua sempre disponível para ir além disto.
    maxAutoPage: 20,

    init() {
      this.overlay  = document.querySelector('.cloud-search-overlay');
      this.trigger  = document.querySelector('[data-search-trigger]');
      this.closeBtn = document.querySelector('[data-search-close]');
      this.input    = document.querySelector('.cloud-search-overlay input[type="text"]');
      this.results  = document.querySelector('.cloud-search-results');

      if (!this.overlay || !this.trigger) return;

      // Controlado por Theme Settings > Search > "Mostrar preço na pesquisa
      // rápida" (desligado por omissão). A lógica de formatação do preço
      // (formatPrice) está correta e pronta — só a exibição fica escondida.
      this.showPrice = this.overlay.dataset.showPrice === 'true';

      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });

      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

      // Backdrop close
      this.overlay.querySelector('.cloud-search-overlay__backdrop')?.addEventListener('click', () => this.close());

      // ESC to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      // Input handler
      if (this.input) {
        this.input.addEventListener('input', () => {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(() => this.search(this.input.value), 300);
        });
      }

      // Scroll infinito: a lista de produtos tem o seu próprio scroll
      // interno (ver .cloud-search-results em cloud-base.css), por isso o
      // listener vai aqui, não em window.
      if (this.results) {
        this.results.addEventListener('scroll', () => this.maybeLoadMore(), { passive: true });
      }
    },

    open() {
      this.overlay.classList.add('cloud-search-overlay--open');
      document.body.style.overflow = 'hidden';
      setTimeout(() => this.input?.focus(), 100);
    },

    close() {
      this.overlay.classList.remove('cloud-search-overlay--open');
      document.body.style.overflow = '';
    },

    async search(query) {
      if (!query || query.length < 2) {
        if (this.results) this.results.innerHTML = '';
        this.currentQuery = null;
        return;
      }

      const token = ++this.searchToken;
      this.currentQuery = query;
      this.nextPage = 2; // a página 1 é este pedido (suggest.json); loadMore() começa na 2
      this.loadingMore = false;
      this.noMoreResults = false;

      try {
        // 10 é o limite máximo aceite pela API pública /search/suggest.json
        // por tipo de recurso (product/collection) — não é possível pedir
        // mais por esta via num único pedido. O scroll infinito (loadMore)
        // continua a partir da página 2 usando o índice de pesquisa
        // completo (templates/search.cloud-search-json.liquid).
        const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=10`;
        const res = await fetch(url);
        const data = await res.json();
        if (token !== this.searchToken) return; // pesquisa mais recente já respondeu
        this.renderResults(data.resources?.results || {}, query);
      } catch (err) {
        console.warn('Cloud search error:', err);
      }
    },

    renderResults({ products = [], collections = [] }, query) {
      if (!this.results) return;

      if (!products.length && !collections.length) {
        this.results.innerHTML = `<p class="cloud-search-empty cloud-text-muted cloud-body-sm" style="padding:var(--space-lg) 0">Nenhum resultado encontrado.</p>`;
        this.noMoreResults = true;
        return;
      }

      let html = '';

      if (products.length) {
        html += `<div class="cloud-search-group">
          <p class="cloud-label cloud-mb-md">Produtos</p>
          <div class="cloud-search-products">`;
        products.forEach(p => {
          const priceMarkup = this.showPrice
            ? `<p class="cloud-search-product__price cloud-text-secondary cloud-body-sm">${this.formatPrice(p.price)}</p>`
            : '';
          html += `<a href="${p.url}" class="cloud-search-product">
            <div class="cloud-search-product__img">
              <img src="${p.featured_image?.url || ''}" alt="${p.title}" loading="lazy">
            </div>
            <div class="cloud-search-product__info">
              <p class="cloud-search-product__name">${p.title}</p>
              ${priceMarkup}
            </div>
          </a>`;
        });
        html += `<p class="cloud-search-loading-more" hidden>A carregar mais...</p>`;
        html += `</div></div>`;
        // Menos de 10 = já é a lista toda; não há página 2 para ir buscar.
        if (products.length < 10) this.noMoreResults = true;
      } else {
        this.noMoreResults = true;
      }

      if (collections.length) {
        html += `<div class="cloud-search-group cloud-mt-lg">
          <p class="cloud-label cloud-mb-md">Coleções</p>
          <div class="cloud-search-collections">`;
        collections.forEach(c => {
          html += `<a href="${c.url}" class="cloud-search-collection">${c.title}</a>`;
        });
        html += `</div></div>`;
      }

      this.results.innerHTML = html;
    },

    // Chamado a cada scroll de #cloud-search-results — só age quando o
    // utilizador está perto do fim da lista já carregada.
    maybeLoadMore() {
      if (!this.results || this.loadingMore || this.noMoreResults || !this.currentQuery) return;
      const nearBottom = this.results.scrollTop + this.results.clientHeight >= this.results.scrollHeight - 150;
      if (nearBottom) this.loadMore();
    },

    // Vai buscar a página seguinte de produtos ao índice de pesquisa
    // completo (não ao suggest.json, que está capado em 10 — ver search())
    // e insere-a antes do indicador "A carregar mais...", que fica sempre
    // como o último elemento da lista.
    async loadMore() {
      const query = this.currentQuery;
      const token = this.searchToken;
      const productsBox = this.results.querySelector('.cloud-search-products');
      if (!productsBox) { this.noMoreResults = true; return; }

      if (this.nextPage > this.maxAutoPage) {
        this.noMoreResults = true;
        return;
      }

      this.loadingMore = true;
      const loadingEl = productsBox.querySelector('.cloud-search-loading-more');
      if (loadingEl) loadingEl.hidden = false;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = `${root}search?q=${encodeURIComponent(query)}&type=product&view=cloud-search-json&page=${this.nextPage}`;
        const res = await fetch(url);
        const text = await res.text();
        if (token !== this.searchToken) return; // pesquisa mudou entretanto

        const temp = document.createElement('div');
        temp.innerHTML = text;
        const newItems = temp.querySelectorAll('.cloud-search-product');

        if (loadingEl) loadingEl.hidden = true;

        if (!newItems.length) {
          this.noMoreResults = true;
          return;
        }

        newItems.forEach(item => {
          if (loadingEl) productsBox.insertBefore(item, loadingEl);
          else productsBox.appendChild(item);
        });

        this.nextPage += 1;
        // A página de pesquisa completa devolve sempre `by 10` (ver
        // templates/search.cloud-search-json.liquid) — menos de 10 novos
        // itens significa que esta foi a última página.
        if (newItems.length < 10) this.noMoreResults = true;
      } catch (err) {
        console.warn('Cloud search — erro ao carregar mais resultados:', err);
        if (loadingEl) loadingEl.hidden = true;
      } finally {
        this.loadingMore = false;
      }
    },

    formatPrice(price) {
      // A API /search/suggest.json devolve o preço já no valor "grande"
      // (ex: "29.90"), não em cêntimos — dividir por 100 aqui dava
      // €0,30 em vez de €29,90. Não dividir, só formatar.
      if (!price) return '';
      const amount = parseFloat(price);
      if (Number.isNaN(amount)) return '';
      const currency = window.Shopify?.currency?.active || 'EUR';
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
    }
  };

  /* =========================================================================
     6. Product Page
     ========================================================================= */
  const CloudProduct = {
    init() {
      this.initGallery();
      this.initVariants();
      this.initQuantity();
      this.initAccordion();
      this.initStickyBuy();
    },

    initGallery() {
      const thumbs = document.querySelectorAll('.cloud-product-gallery__thumb');
      const main   = document.querySelector('.cloud-product-gallery__main img');
      if (!thumbs.length || !main) return;

      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          // data-thumb já vem em 900px (gerado pelo Liquid só para isto) —
          // usar thumb.querySelector('img').src pegava na miniatura pequena
          // (200px). "_thumb"/"_large" também nunca existiu nos URLs do CDN
          // da Shopify (usam ?width=N), por isso o replace() não fazia nada.
          const src = thumb.dataset.thumb;
          if (src) {
            main.style.opacity = '0';
            setTimeout(() => {
              main.src = src;
              // A tag <img> tem srcset (gerado pelo Liquid para a imagem
              // inicial) — o browser ignora por completo um novo `src`
              // enquanto o srcset antigo continuar a apontar para a imagem
              // anterior (mesmo bug já corrigido em initVariants/aplicar()).
              // Regenerar o srcset a partir do mesmo data-thumb, trocando só
              // o width=.
              main.srcset = [600, 900, 1200]
                .map(w => `${src.replace(/([?&])width=\d+/, `$1width=${w}`)} ${w}w`)
                .join(', ');
              main.style.opacity = '1';
            }, 200);
          }
          thumbs.forEach(t => t.classList.remove('cloud-product-gallery__thumb--active'));
          thumb.classList.add('cloud-product-gallery__thumb--active');
        });
      });

      if (thumbs[0]) thumbs[0].classList.add('cloud-product-gallery__thumb--active');
    },

    initVariants() {
      const input   = document.getElementById('variant-id-input');
      const dataEl  = document.getElementById('cloud-variant-data');
      const grupos  = [...document.querySelectorAll('.cloud-variant-group')];
      if (!input || !dataEl || !grupos.length) return;

      let variantes = [];
      try {
        variantes = JSON.parse(dataEl.textContent);
      } catch (e) {
        return; // sem dados válidos, não mexer em nada
      }
      if (!variantes.length) return;

      const addBtn  = document.querySelector('[data-add-to-cart]');
      const precoEl = document.querySelector('.cloud-product-info__price-current');
      const mainImg = document.getElementById('product-gallery-main-img');
      const moeda   = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';

      const formatar = cents => {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'pt-PT',
            { style: 'currency', currency: moeda }).format(cents / 100);
        } catch (e) {
          return (cents / 100).toFixed(2);
        }
      };

      // Valores escolhidos, um por grupo de opção, pela ordem das opções.
      // Cada grupo só contém UM tipo de botão (swatch de cor OU tamanho),
      // conforme o Liquid decide em options_with_values — mas esta função
      // só procurava .cloud-size-btn--active, nunca .cloud-swatch--active.
      // Num produto cuja única opção é cor (ex: óculos com só "Cor"), isto
      // devolvia sempre null, encontrarVariante() nunca encontrava a
      // variante, e aplicar() saía sem trocar a imagem principal — o clique
      // no swatch mudava o estado visual do botão mas nada mais.
      const escolhidas = () => grupos.map(g => {
        const activo = g.querySelector('.cloud-size-btn--active, .cloud-swatch--active');
        return activo ? activo.dataset.value : null;
      });

      const encontrarVariante = vals => variantes.find(v =>
        v.options.length === vals.length && v.options.every((o, i) => o === vals[i])
      );

      // Escreve a variante escolhida onde o Shopify a vai ler de facto
      const aplicar = () => {
        const v = encontrarVariante(escolhidas());
        if (!v) return;

        input.value = v.id;                       // usado pelo submit e pelo "Comprar Agora"
        if (addBtn) {
          addBtn.dataset.variantId = v.id;        // usado pelo CloudCart (fetch /cart/add.js)
          addBtn.disabled = !v.available;
          addBtn.setAttribute('aria-disabled', String(!v.available));
        }
        if (precoEl) precoEl.textContent = formatar(v.price);
        if (v.image && mainImg) {
          // A tag <img> tem `srcset` (gerado pelo Liquid para a primeira
          // variante, ex: largura 600/900/1200). Quando o browser suporta
          // srcset ele ignora por completo o `src` para escolher a imagem —
          // por isso só trocar `mainImg.src` nunca mudava nada visualmente,
          // mesmo com o valor certo já lá dentro (confirmável por
          // mainImg.currentSrc, que continuava a apontar para o srcset velho).
          // É preciso regenerar o srcset com a imagem da variante, trocando
          // só o parâmetro width= de cada largura.
          mainImg.src = v.image;
          mainImg.srcset = [600, 900, 1200]
            .map(w => `${v.image.replace(/([?&])width=\d+/, `$1width=${w}`)} ${w}w`)
            .join(', ');
        }

        // A galeria salta para a foto desta variante e marca a miniatura dela
        const thumbs = [...document.querySelectorAll('.cloud-product-gallery__thumb')];
        const alvo = thumbs.find(t =>
          (t.dataset.variantIds || '').split(',').includes(String(v.id))
        );
        if (alvo) {
          thumbs.forEach(t => t.classList.remove('cloud-product-gallery__thumb--active'));
          alvo.classList.add('cloud-product-gallery__thumb--active');
          // block:'nearest' evita que a página salte na vertical; o scroll
          // acontece dentro da própria tira de miniaturas.
          alvo.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      };

      // Swatches de cor e botões de tamanho partilham a mesma lógica,
      // só muda a classe de "activo".
      const SELECTOR_BTN = '.cloud-size-btn, .cloud-swatch';
      const classeActiva = el =>
        el.classList.contains('cloud-swatch') ? 'cloud-swatch--active' : 'cloud-size-btn--active';

      grupos.forEach(grupo => {
        const btns  = [...grupo.querySelectorAll(SELECTOR_BTN)];
        const label = grupo.querySelector('.cloud-variant-group__selected');
        if (!btns.length) return;

        btns.forEach(btn => {
          btn.addEventListener('click', () => {
            // O activo só troca DENTRO do próprio grupo — antes trocava em
            // todos os grupos, o que partia produtos com mais de uma opção.
            btns.forEach(b => {
              b.classList.remove(classeActiva(b));
              b.setAttribute('aria-pressed', 'false');
            });
            btn.classList.add(classeActiva(btn));
            btn.setAttribute('aria-pressed', 'true');
            if (label) label.textContent = btn.dataset.value;
            aplicar();
          });
        });

        // Se o grupo abrir sem nada activo, activar o primeiro
        const jaActivo = btns.some(b => b.classList.contains(classeActiva(b)));
        if (!jaActivo) {
          btns[0].classList.add(classeActiva(btns[0]));
          btns[0].setAttribute('aria-pressed', 'true');
          if (label) label.textContent = btns[0].dataset.value;
        }
      });

      aplicar(); // sincronizar o estado inicial
    },

    initQuantity() {
      document.querySelectorAll('.cloud-quantity').forEach(wrap => {
        const input  = wrap.querySelector('.cloud-quantity__input');
        const minus  = wrap.querySelector('[data-qty-minus]');
        const plus   = wrap.querySelector('[data-qty-plus]');
        if (!input) return;

        if (minus) minus.addEventListener('click', () => {
          const val = parseInt(input.value);
          if (val > 1) input.value = val - 1;
        });

        if (plus) plus.addEventListener('click', () => {
          const val = parseInt(input.value);
          input.value = val + 1;
        });
      });
    },

    initAccordion() {
      // max-height é medido a partir do conteúdo real (não um valor CSS fixo),
      // porque a descrição pode conter imagens altas (ex: banner de garantia
      // dos óculos) cuja altura varia com a largura da coluna — um valor fixo
      // corta o conteúdo em larguras onde a imagem renderiza mais alta.
      const expandir = (content) => {
        if (!content) return;
        content.style.maxHeight = content.scrollHeight + 'px';
        content.querySelectorAll('img[loading="lazy"]').forEach(img => {
          // Dentro de um accordion recém-aberto pelo utilizador, o lazy-load
          // nativo não é fiável: o browser mede a proximidade do viewport
          // usando a caixa colapsada (altura 0) de antes da imagem carregar,
          // e o evento "load" pode nunca disparar. O utilizador já pediu
          // para ver o conteúdo, por isso força carregamento imediato aqui.
          img.loading = 'eager';
        });
        content.querySelectorAll('img').forEach(img => {
          if (!img.complete) {
            img.addEventListener('load', () => {
              if (content.closest('.cloud-accordion__item--open')) {
                content.style.maxHeight = content.scrollHeight + 'px';
              }
            }, { once: true });
          }
        });
      };

      document.querySelectorAll('.cloud-accordion__trigger').forEach(trigger => {
        trigger.addEventListener('click', () => {
          const item = trigger.closest('.cloud-accordion__item');
          const isOpen = item.classList.contains('cloud-accordion__item--open');

          // Close all
          document.querySelectorAll('.cloud-accordion__item--open').forEach(openItem => {
            openItem.classList.remove('cloud-accordion__item--open');
            const openContent = openItem.querySelector('.cloud-accordion__content');
            if (openContent) openContent.style.maxHeight = '';
          });

          // Toggle clicked
          if (!isOpen) {
            item.classList.add('cloud-accordion__item--open');
            expandir(item.querySelector('.cloud-accordion__content'));
          }
        });
      });

      // Open first by default
      const primeiro = document.querySelector('.cloud-accordion__item');
      if (primeiro) {
        primeiro.classList.add('cloud-accordion__item--open');
        expandir(primeiro.querySelector('.cloud-accordion__content'));
      }
    },

    initStickyBuy() {
      const stickyBar = document.querySelector('.cloud-sticky-buy');
      const purchaseBox = document.querySelector('.cloud-product-info');
      if (!stickyBar || !purchaseBox) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          stickyBar.style.opacity = entry.isIntersecting ? '0' : '1';
          stickyBar.style.pointerEvents = entry.isIntersecting ? 'none' : 'all';
        });
      }, { threshold: 0 });

      observer.observe(purchaseBox);
    }
  };

  /* =========================================================================
     7. Collection Filters (Preço Mín/Máx — hoje na toolbar horizontal,
     sem sidebar)
     ========================================================================= */
  const CloudFilters = {
    init() {
      // Preço (Mín/Máx): submete o formulário ao sair do campo (valor
      // alterado) ou ao premir Enter — não a cada tecla, para não recarregar
      // a página a meio da digitação de um número.
      document.querySelectorAll('[data-price-filter]').forEach(input => {
        input.addEventListener('change', () => input.form?.submit());
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            input.form?.submit();
          }
        });
      });
    }
  };

  /* =========================================================================
     7b. Collection Search (escopada à COLEÇÃO inteira, não só à página
     atual). A grelha só tem os produtos da página corrente (paginação),
     por isso ao pesquisar buscamos em background as restantes páginas da
     mesma coleção (preservando filtros/ordenação já aplicados via
     window.location.search) e construímos um índice título→HTML do card
     já renderizado pelo servidor. Constrói-se uma única vez por visita à
     página, na primeira pesquisa.
     ========================================================================= */
  const CloudCollectionSearch = {
    MAX_PAGES: 50,

    init() {
      const input = document.getElementById('cloud-collection-search-input');
      const grid = document.getElementById('product-grid');
      if (!input || !grid) return;

      const countEl = document.querySelector('#cloud-collection-count [data-count-value]');
      const totalCount = countEl ? countEl.textContent.trim() : null;
      const emptyState = document.getElementById('cloud-collection-search-empty');
      const paginationEl = document.querySelector('[data-collection-pagination]');
      const totalPages = Math.min(parseInt(grid.dataset.totalPages, 10) || 1, this.MAX_PAGES);
      const originalGridHTML = grid.innerHTML;

      // O parâmetro section_id da Section Rendering API do Shopify precisa
      // do ID completo e qualificado pelo template (ex.:
      // "template--123__main-collection-product-grid"), não só do tipo da
      // secção — usar só o tipo falha silenciosamente em alguns templates
      // (a resposta vem sem o grid, o índice fica vazio e a pesquisa não
      // encontra nada, mesmo com produtos correspondentes). Vamos buscá-lo
      // ao wrapper real da secção no DOM em vez de o fixar no código.
      const sectionWrapper = grid.closest('[id^="shopify-section-"]');
      const sectionId = sectionWrapper
        ? sectionWrapper.id.replace(/^shopify-section-/, '')
        : 'main-collection-product-grid';

      let indexPromise = null;
      let debounceTimer = null;

      function parseCardsFromHTML(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const pageGrid = doc.getElementById('product-grid');
        if (!pageGrid) return [];
        return Array.from(pageGrid.querySelectorAll('[data-product-title]')).map((el) => ({
          title: (el.dataset.productTitle || '').toLowerCase(),
          html: el.outerHTML
        }));
      }

      function buildIndex() {
        if (indexPromise) return indexPromise;

        const requests = [];
        for (let page = 1; page <= totalPages; page += 1) {
          const url = new URL(window.location.href);
          url.searchParams.set('page', page);
          url.searchParams.set('section_id', sectionId);
          requests.push(
            fetch(url.toString())
              .then((r) => r.text())
              .then(parseCardsFromHTML)
              .catch(() => [])
          );
        }

        indexPromise = Promise.all(requests).then((pages) => pages.flat());
        return indexPromise;
      }

      function setLoading(isLoading) {
        input.classList.toggle('cloud-collection-search--loading', isLoading);
      }

      // .cloud-reveal começa a opacity:0 e só fica visível quando o
      // IntersectionObserver do CloudReveal lhe adiciona --visible ao
      // entrar no viewport — mas esse observer só é registado uma vez, no
      // carregamento da página, para os elementos que existem nesse
      // momento. Cards injetados depois via innerHTML (resultado da
      // pesquisa, ou o grid restaurado) nunca são observados, por isso
      // ficam transparentes para sempre — a contagem fica certa, mas a
      // grid parece vazia. Marcamos --visible manualmente aqui.
      function markRevealed(container) {
        container.querySelectorAll('.cloud-reveal').forEach((el) => {
          el.classList.add('cloud-reveal--visible');
        });
      }

      function renderMatches(matches, query) {
        grid.innerHTML = matches.map((m) => m.html).join('');
        markRevealed(grid);
        if (countEl) countEl.textContent = query === '' ? totalCount : matches.length;
        if (emptyState) emptyState.hidden = !(query !== '' && matches.length === 0);
        if (paginationEl) paginationEl.style.display = query === '' ? '' : 'none';
      }

      function restoreOriginal() {
        grid.innerHTML = originalGridHTML;
        markRevealed(grid);
        if (countEl) countEl.textContent = totalCount;
        if (emptyState) emptyState.hidden = true;
        if (paginationEl) paginationEl.style.display = '';
      }

      function applyFilter() {
        const query = input.value.trim().toLowerCase();

        if (query === '') {
          setLoading(false);
          restoreOriginal();
          return;
        }

        setLoading(true);
        buildIndex().then((index) => {
          setLoading(false);
          // O cliente pode ter continuado a escrever enquanto as páginas
          // carregavam — usa sempre o valor mais recente do campo.
          const currentQuery = input.value.trim().toLowerCase();
          if (currentQuery === '') {
            restoreOriginal();
            return;
          }
          const matches = index.filter((item) => item.title.includes(currentQuery));
          renderMatches(matches, currentQuery);
        });
      }

      input.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(applyFilter, 300);
      });
    }
  };

  /* =========================================================================
     8. Add to Cart with Animation
     ========================================================================= */
  const CloudCart = {
    init() {
      document.querySelectorAll('[data-add-to-cart]').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          const variantId = btn.dataset.variantId;
          if (!variantId) return;

          // A quantidade tem de vir do formulário — estava fixa a 1, por isso
          // o cliente podia pedir 3 e receber 1.
          const form = btn.closest('form');
          const qtyEl = form ? form.querySelector('[name="quantity"]') : null;
          const quantity = qtyEl ? Math.max(1, parseInt(qtyEl.value, 10) || 1) : 1;

          // Line item properties (ex: personalização de camisa — nome/
          // número/patches, ver snippets/jersey-customization.liquid).
          // Este fetch construía o body à mão só com id/quantity, por isso
          // qualquer campo `properties[...]` do formulário era ignorado e
          // nunca chegava ao carrinho — o "Comprar Agora" ao lado funciona
          // porque faz um submit nativo do form, não um fetch manual.
          // FormData apanha qualquer properties[X] automaticamente (e já
          // ignora inputs disabled, como o Nome/Número quando a aba
          // "Escolher Jogador" está ativa).
          const properties = {};
          if (form) {
            new FormData(form).forEach((value, key) => {
              const match = key.match(/^properties\[(.+)\]$/);
              if (match && value !== '') {
                properties[match[1]] = value;
              }
            });
          }

          const originalText = btn.textContent;
          btn.textContent = 'Adicionando...';
          btn.disabled = true;

          try {
            const payload = { id: variantId, quantity: quantity };
            if (Object.keys(properties).length) {
              payload.properties = properties;
            }
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
            });

            btn.textContent = '✓ Adicionado';
            btn.style.background = 'var(--cloud-success)';

            // Update cart count
            const countRes = await fetch('/cart.js');
            const cartData = await countRes.json();
            document.querySelectorAll('.cloud-header__cart-count').forEach(badge => {
              badge.textContent = cartData.item_count;
              badge.hidden = cartData.item_count === 0;
            });

            // Abre o cart drawer (assets/cloud-cart-drawer.js) já com o
            // item novo, se o drawer estiver ativo neste tema.
            document.dispatchEvent(new CustomEvent('cloud:cart:added'));

            setTimeout(() => {
              btn.textContent = originalText;
              btn.style.background = '';
              btn.disabled = false;
            }, 2000);

          } catch (err) {
            btn.textContent = 'Erro';
            btn.style.background = 'var(--cloud-danger)';
            setTimeout(() => {
              btn.textContent = originalText;
              btn.style.background = '';
              btn.disabled = false;
            }, 2000);
          }
        });
      });
    }
  };

  /* =========================================================================
     9. Search UI Styles (injected for search result panel)
     ========================================================================= */
  function injectSearchStyles() {
    const style = document.createElement('style');
    style.textContent = `
      .cloud-search-group { margin-bottom: var(--space-lg); }
      .cloud-search-products { display: flex; flex-direction: column; gap: var(--space-sm); }
      .cloud-search-product {
        display: flex; align-items: center; gap: var(--space-md);
        padding: var(--space-sm); border-radius: var(--radius-md);
        transition: background var(--transition-fast);
        text-decoration: none;
      }
      .cloud-search-product:hover { background: var(--cloud-card); }
      .cloud-search-product__img {
        width: 56px; height: 56px; border-radius: var(--radius-sm);
        overflow: hidden; flex-shrink: 0; background: var(--cloud-card);
      }
      .cloud-search-product__img img { width: 100%; height: 100%; object-fit: cover; }
      .cloud-search-product__name { font-size: 14px; font-weight: 500; color: var(--cloud-text); margin-bottom: 2px; }
      .cloud-search-collections { display: flex; flex-wrap: wrap; gap: var(--space-sm); }
      .cloud-search-collection {
        padding: 6px 16px; background: var(--cloud-card); border: 1px solid var(--cloud-border);
        border-radius: var(--radius-full); font-size: 13px; color: var(--cloud-text-secondary);
        transition: all var(--transition-fast); text-decoration: none;
      }
      .cloud-search-collection:hover { border-color: var(--cloud-accent); color: var(--cloud-accent); }
      .cloud-search-loading-more {
        text-align: center; padding: var(--space-sm) 0;
        font-size: 12px; color: var(--cloud-text-muted);
      }
    `;
    document.head.appendChild(style);
  }

  /* =========================================================================
     10. Init All on DOM Ready
     ========================================================================= */
  function init() {
    CloudHeader.init();
    CloudMegaMenu.init();
    CloudMobileNav.init();
    CloudHeroCarousel.init();
    CloudSlider.init();
    CloudReveal.init();
    CloudSearch.init();
    CloudProduct.init();
    CloudFilters.init();
    CloudCollectionSearch.init();
    CloudCart.init();
    injectSearchStyles();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
